import { useState } from "react";
import { Phone, MessageCircle, Calendar, Users, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reservation = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    guests: 2,
    date: "",
    time: "",
    message: "",
  });

  const timeSlots = [
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "20:00",
    "20:30",
    "21:00",
    "21:30",
    "22:00",
    "22:30",
    "23:00",
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/reservations`, formData);
      if (response.data.id) {
        toast.success(
          "¡Reserva realizada! Te contactaremos para confirmar los detalles."
        );
        setFormData({
          name: "",
          phone: "",
          guests: 2,
          date: "",
          time: "",
          message: "",
        });
      }
    } catch (error) {
      toast.error("Error al realizar la reserva. Por favor, inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(
      `Hola, me gustaría reservar mesa en Abacería Como en Casa.\n\nNombre: ${formData.name || "[Tu nombre]"}\nPersonas: ${formData.guests}\nFecha: ${formData.date || "[Fecha]"}\nHora: ${formData.time || "[Hora]"}`
    );
    window.open(`https://wa.me/34672729957?text=${message}`, "_blank");
  };

  // Get tomorrow's date as minimum
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split("T")[0];

  return (
    <section id="reservas" className="section-padding bg-[#F9F7F2]">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Content */}
          <div>
            <p className="font-accent text-[#800020] text-lg mb-4">
              Tu mesa te espera
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-[#1A1A1A] mb-6">
              Dónde Cenar en Sevilla Este
            </h2>

            <p className="font-body text-[#1A1A1A]/80 leading-relaxed mb-8">
              Si te gusta el buen vino, las tapas gourmet y el ambiente auténtico de
              una abacería andaluza, Abacería Como en Casa es tu lugar.
            </p>

            {/* Contact Options */}
            <div className="space-y-4 mb-8">
              <a
                href="tel:+34672729957"
                data-testid="call-btn"
                className="flex items-center gap-4 p-4 bg-white border border-[#DCD7CC] hover:border-[#800020] transition-colors"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#800020] text-white">
                  <Phone size={20} />
                </div>
                <div>
                  <span className="font-body text-sm text-[#1A1A1A]/60 block">
                    Llámanos
                  </span>
                  <span className="font-heading text-lg text-[#1A1A1A]">
                    672 72 99 57
                  </span>
                </div>
              </a>

              <button
                onClick={handleWhatsApp}
                data-testid="whatsapp-btn"
                className="w-full flex items-center gap-4 p-4 bg-white border border-[#DCD7CC] hover:border-[#25D366] transition-colors"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-[#25D366] text-white">
                  <MessageCircle size={20} />
                </div>
                <div className="text-left">
                  <span className="font-body text-sm text-[#1A1A1A]/60 block">
                    WhatsApp
                  </span>
                  <span className="font-heading text-lg text-[#1A1A1A]">
                    Reserva por WhatsApp
                  </span>
                </div>
              </button>
            </div>

            {/* Schedule */}
            <div className="bg-white p-6 border border-[#DCD7CC]">
              <h3 className="font-heading text-lg text-[#1A1A1A] mb-4 flex items-center gap-2">
                <Clock size={18} className="text-[#800020]" />
                Horario
              </h3>
              <div className="space-y-0">
                <div className="schedule-row">
                  <span className="schedule-day font-body">Jueves</span>
                  <span className="schedule-time font-body">20:00 - 24:00</span>
                </div>
                <div className="schedule-row">
                  <span className="schedule-day font-body">Viernes</span>
                  <span className="schedule-time font-body">
                    13:00 - 17:00, 20:00 - 24:00
                  </span>
                </div>
                <div className="schedule-row">
                  <span className="schedule-day font-body">Sábado</span>
                  <span className="schedule-time font-body">
                    13:00 - 17:00, 20:00 - 24:00
                  </span>
                </div>
                <div className="schedule-row">
                  <span className="schedule-day font-body">Domingo</span>
                  <span className="schedule-time font-body">13:00 - 18:00</span>
                </div>
                <div className="schedule-row">
                  <span className="schedule-day font-body">Lun - Mié</span>
                  <span className="schedule-closed font-body">Cerrado</span>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white p-8 border border-[#DCD7CC]">
            <h3 className="font-heading text-2xl text-[#1A1A1A] mb-6">
              Formulario de Reserva
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="font-body text-sm text-[#1A1A1A]/70 mb-2 block">
                  Nombre *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  data-testid="reservation-name"
                  className="form-input-elegant"
                  placeholder="Tu nombre"
                />
              </div>

              <div>
                <label className="font-body text-sm text-[#1A1A1A]/70 mb-2 block">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  data-testid="reservation-phone"
                  className="form-input-elegant"
                  placeholder="Tu teléfono"
                />
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="font-body text-sm text-[#1A1A1A]/70 mb-2 flex items-center gap-2">
                    <Users size={14} />
                    Personas *
                  </label>
                  <select
                    required
                    value={formData.guests}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        guests: parseInt(e.target.value),
                      })
                    }
                    data-testid="reservation-guests"
                    className="form-input-elegant cursor-pointer"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((num) => (
                      <option key={num} value={num}>
                        {num} {num === 1 ? "persona" : "personas"}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="font-body text-sm text-[#1A1A1A]/70 mb-2 flex items-center gap-2">
                    <Calendar size={14} />
                    Fecha *
                  </label>
                  <input
                    type="date"
                    required
                    min={minDate}
                    value={formData.date}
                    onChange={(e) =>
                      setFormData({ ...formData, date: e.target.value })
                    }
                    data-testid="reservation-date"
                    className="form-input-elegant cursor-pointer"
                  />
                </div>
              </div>

              <div>
                <label className="font-body text-sm text-[#1A1A1A]/70 mb-2 flex items-center gap-2">
                  <Clock size={14} />
                  Hora *
                </label>
                <select
                  required
                  value={formData.time}
                  onChange={(e) =>
                    setFormData({ ...formData, time: e.target.value })
                  }
                  data-testid="reservation-time"
                  className="form-input-elegant cursor-pointer"
                >
                  <option value="">Selecciona una hora</option>
                  {timeSlots.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="font-body text-sm text-[#1A1A1A]/70 mb-2 block">
                  Comentarios (opcional)
                </label>
                <textarea
                  value={formData.message}
                  onChange={(e) =>
                    setFormData({ ...formData, message: e.target.value })
                  }
                  data-testid="reservation-message"
                  className="form-input-elegant resize-none"
                  rows={3}
                  placeholder="Alergias, preferencias, celebración especial..."
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="submit-reservation"
                className="btn-primary w-full"
              >
                {loading ? "Enviando..." : "Reservar Mesa"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reservation;
